import Auth from '@getgo/auth-client';
import AppConstants from '../AppConstants';
import uuidV4 from 'uuid/v4';
import { Base64 } from 'js-base64';
import { clearSessionStorage, getSessionStorage, setSessionStorage } from '../Utils/SessionStorage';

const EXPIRY_INSTANT_KEY = 'expiry_instant';
const ACCESS_TOKEN_KEY = 'access_token';
const AUTH_STATE = 'auth_state';
const CODE_VERIFIER = 'code_verifier';
const SEND_MIXPANEL_LOGIN_EVENT = 'sendMixpanelLoginEvent';

const options = {
  client_id: AppConstants.AUTH_CLIENT_ID,
  url: AppConstants.AUTH_SERVICE_BASE_URL,
  redirect_url: AppConstants.AUTH_REDIRECT_URL
};
const auth = new Auth(options);

let token;

export function login(location) {
  clearAccessToken();
  clearExpiryInstant();
  let { pathname, search, hash } = location || window.location;
  let uuid = uuidV4();

  // Initial visit, start login process
  const state = Base64.encodeURI(JSON.stringify({ uuid, pathname, search, hash }));
  const codeVerifier = auth.randomString(64);

  setSessionStorage(AUTH_STATE, state);
  setSessionStorage(CODE_VERIFIER, codeVerifier);

  auth.loginWithPKCE({ state, code_verifier: codeVerifier });
}

export async function verifyStateAndGetRedirectLocation() {
  let finalRedirection = '/';

  const urlParams = new URLSearchParams(window.location.search);
  const stateFromLocation = urlParams.get('state');

  if (stateFromLocation) {
    // 2. Redirect back from the authorization server.
    // SECURITY CHECK: Verify the 'state' parameter to prevent CSRF attacks.
    if (getSessionStorage(AUTH_STATE) !== stateFromLocation) {
      throw new Error('State parameter mismatch. Possible session hijacking attack!');
    }

    // Get the authorization code from the URL.
    const authorizationCode = urlParams.get('code');
    if (!authorizationCode) {
      throw new Error('Authorization failed: No authorization code was returned.');
    }

    // Retrieve the original code_verifier from storage.
    const initialCodeVerifier = getSessionStorage(CODE_VERIFIER);

    // Exchange the authorization code for an access token.
    const config = {
      state: stateFromLocation,
      code: authorizationCode,
      code_verifier: initialCodeVerifier
    };

    try {
      const requestPKCEToken = await auth.requestPKCEToken(config);
      // SUCCESS: Token received. Store it securely.
      finalRedirection = getRedirectionString(getSessionStorage(AUTH_STATE)) || finalRedirection;
      token = requestPKCEToken;
      setAccessToken();
      setExpiryInstant();
      setMixpanelLoginEventToBeSent('true');

      clearSessionStorage(AUTH_STATE);
      clearSessionStorage(CODE_VERIFIER);
    } catch (error) {
      throw new Error('Authorization failed: Token request failed.');
    }
  }

  return finalRedirection;
}

function getRedirectionString(authState) {
  let { pathname, search, hash } = JSON.parse(Base64.decode(authState));
  return [pathname, search, hash].join('');
}

export function logout() {
  clearAccessToken();
  clearExpiryInstant();
  auth.logout();
}

export function isLoggedIn() {
  const expiryInstantString = getExpiryInstant();
  const accessToken = getAccessToken();
  return accessToken && expiryInstantString && Number(expiryInstantString) > Date.now();
}

export function getExpiryInstant() {
  return localStorage.getItem(EXPIRY_INSTANT_KEY);
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function clearExpiryInstant() {
  localStorage.removeItem(EXPIRY_INSTANT_KEY);
}

function clearAccessToken() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
}

function setAccessToken() {
  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token.access_token);
  }
}

function setExpiryInstant() {
  if (token) {
    localStorage.setItem(EXPIRY_INSTANT_KEY, token.expires.toString());
  }
}

export function setMixpanelLoginEventToBeSent(value) {
  localStorage.setItem(SEND_MIXPANEL_LOGIN_EVENT, value);
}

export function shouldMixpanelLoginEventBeSent() {
  return (
    localStorage.getItem(SEND_MIXPANEL_LOGIN_EVENT) !== null &&
    localStorage.getItem(SEND_MIXPANEL_LOGIN_EVENT) === 'true'
  );
}



// WEBPACK FOOTER //
// ./src/Authentication/AuthenticationService.js